// /onboarding/workdays — «ما أيام العمل؟» (undrawn — ruling W1): shape 3's
// step 5. The whole business is booked by period, so the days carry no
// times: one toggle per day from Saturday, «عطلة · مغلق» on the weekend row.
// The submit writes the business (ensureBusiness) and then PUT /hours with
// 0–1440 on every open day (ruling R13: periods read only HoursForDate.Open).
// States: default · invalid · saving (· error cards through the shell).

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { DayRows } from '../components/DayRows';
import { Msg } from '../components/widgets';
import { Wizard } from '../components/Wizard';
import { HOURS, SHELL, WORKDAYS } from '../copy';
import { putHours } from '../data/api';
import { afterWriting } from '../data/enroll';
import { ONB } from '../data/flow';
import { onboardingStore, useOnboarding, workdaysPayload } from '../state';
import { useBusinessPreview } from './Business';
import { useEnrollRun } from './useEnrollRun';

export function WorkdaysScreen() {
  const mobile = useLayout() === 'phone';
  const { hours, draft, createdId } = useOnboarding();
  const { profile, preview } = useBusinessPreview('hours');
  const [error, setError] = useState<string | null>(null);
  // An unwritten business (a failed write, then a reload) stays here to finish it.
  const [existedOnOpen] = useState(() => !!onboardingStore.get().createdId && onboardingStore.get().scheduled);
  const { phase, failure, run } = useEnrollRun((id) => putHours(id, workdaysPayload(onboardingStore.get().hours)));

  if (!sessionStore.isAuthenticated()) return <Navigate to={ONB.phone} replace />;
  // A written business never re-runs this step: on to the shape step until its minimum is met (R8), else done.
  if (createdId && existedOnOpen) return <Navigate to={afterWriting()} replace />;
  if (!draft.name.trim() || !draft.category || !draft.city.trim()) return <Navigate to={ONB.name} replace />;

  const submit = () => {
    if (phase === 'saving') return;
    if (!hours.days.some((d) => d.open)) {
      setError(HOURS.noDay);
      return;
    }
    setError(null);
    void run();
  };

  const rows = (
    <DayRows
      hours={hours}
      timesHidden
      onToggle={(i) => {
        setError(null);
        onboardingStore.setDay(i, { open: !hours.days[i].open });
      }}
      onTime={() => undefined}
      onCopy={() => undefined}
      mobile={mobile}
    />
  );
  const saving = phase === 'saving';

  return (
    <Wizard
      screen="workdays"
      testId="onb-workdays"
      state={saving ? 'saving' : error ? 'invalid' : 'default'}
      list
      question={WORKDAYS.title}
      sub={WORKDAYS.sub}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={saving}>
          {saving ? HOURS.saving : SHELL.continue}
        </PrimaryAction>
      }
      onSubmit={submit}
      error={failure ? { ...failure, onRetry: () => void run() } : null}
      panel={{ phase: 'preview', profile, preview }}
    >
      {mobile ? rows : <div className="onb-listcard">{rows}</div>}
      {error && <Msg testId="onb-hours-error">{error}</Msg>}
    </Wizard>
  );
}
